import { useSyncExternalStore } from 'react';

/**
 * Teeman valinta. Asetus on työasemakohtainen eikä käyttäjäkohtainen: se
 * elää vain selaimen localStoragessa eikä kulje palvelimen kautta. Sama
 * käyttäjä voi siis pitää toimiston näytöllä vaaleaa ja läppärillä tummaa.
 *
 * Kolme vaihtoehtoa, ei kahta: `jarjestelma` on oletus ja säilyttää sen,
 * miten sovellus on tähän asti käyttäytynyt (seuraa käyttöjärjestelmän
 * asetusta). Ilman sitä valinnan tekeminen kerran lukitsisi teeman
 * pysyvästi, vaikka kone vaihtaisi vaaleaan aamulla ja tummaan illalla.
 *
 * Avain ja DOM-arvot on toistettu index.html:n rivikäsikirjoituksessa, joka
 * asettaa teeman ennen ensimmäistä maalausta. Jos muutat niitä, muuta
 * molemmat.
 */
export type Teema = 'vaalea' | 'tumma' | 'jarjestelma';

/** Se, minkä CSS lopulta näkee — `jarjestelma` ratkaistaan aina jommaksikummaksi. */
export type RatkaistuTeema = 'vaalea' | 'tumma';

const AVAIN = 'tk.teema';

const TEEMAT: readonly Teema[] = ['vaalea', 'tumma', 'jarjestelma'];

function onTeema(arvo: unknown): arvo is Teema {
  return typeof arvo === 'string' && (TEEMAT as readonly string[]).includes(arvo);
}

/** Selain voi kieltää localStoragen (yksityinen selaus, evästeasetukset). */
function lueValinta(): Teema {
  try {
    const arvo = localStorage.getItem(AVAIN);
    return onTeema(arvo) ? arvo : 'jarjestelma';
  } catch {
    return 'jarjestelma';
  }
}

function tummaKysely(): MediaQueryList {
  return window.matchMedia('(prefers-color-scheme: dark)');
}

export function ratkaiseTeema(teema: Teema): RatkaistuTeema {
  if (teema !== 'jarjestelma') return teema;
  return tummaKysely().matches ? 'tumma' : 'vaalea';
}

function kirjoitaDomiin(teema: Teema): void {
  document.documentElement.dataset.teema = ratkaiseTeema(teema);
}

let nykyinen: Teema = lueValinta();
const kuuntelijat = new Set<() => void>();

function ilmoita(): void {
  for (const kuuntelija of kuuntelijat) kuuntelija();
}

export function asetaTeema(teema: Teema): void {
  nykyinen = teema;
  try {
    localStorage.setItem(AVAIN, teema);
  } catch {
    // Valinta jää voimaan vain tälle istunnolle. Parempi kuin kaatuminen.
  }
  kirjoitaDomiin(teema);
  ilmoita();
}

function tilaa(kuuntelija: () => void): () => void {
  kuuntelijat.add(kuuntelija);
  return () => {
    kuuntelijat.delete(kuuntelija);
  };
}

/** Valittu vaihtoehto sellaisena kuin käyttäjä sen valitsi. */
export function useTeema(): Teema {
  return useSyncExternalStore(tilaa, () => nykyinen);
}

/** Voimassa oleva teema — `jarjestelma` on tässä jo ratkaistu. */
export function useRatkaistuTeema(): RatkaistuTeema {
  return useSyncExternalStore(tilaa, () => ratkaiseTeema(nykyinen));
}

// Järjestelmän teema voi vaihtua kesken istunnon (esim. ajastettu tumma
// tila illalla). Kun valinta on `jarjestelma`, seurataan sitä myös silloin.
tummaKysely().addEventListener('change', () => {
  if (nykyinen !== 'jarjestelma') return;
  kirjoitaDomiin(nykyinen);
  ilmoita();
});

// Sama työasema, monta välilehteä: valinta on selaimen asetus, joten sen
// kuuluu näkyä kaikissa auki olevissa välilehdissä eikä vain siinä, jossa
// se tehtiin.
window.addEventListener('storage', (tapahtuma) => {
  if (tapahtuma.key !== null && tapahtuma.key !== AVAIN) return;
  nykyinen = lueValinta();
  kirjoitaDomiin(nykyinen);
  ilmoita();
});

/**
 * Asiakasvärit päälle tai pois.
 *
 * Samassa tiedostossa kuin teema, koska kyse on samasta asiasta: miltä
 * sovellus näyttää tällä työasemalla. Valinta ei kulje palvelimen kautta
 * eikä ole käyttäjän ominaisuus.
 *
 * Oletus on `kaytossa`. Värit ovat hienovarainen muutos, ja oletuksena
 * pois ne jäisivät löytymättä — eikä palautetta tulisi siitä, toimivatko
 * ne. Sammuttaminen on silti oltava: sävyt ovat toisille kohinaa, eikä
 * niiden pois ottaminen vie mitään tietoa, koska asiakaskoodi on aina
 * luettavissa (lib/varit.ts).
 *
 * Avain ja DOM-arvot on toistettu index.html:n rivikäsikirjoituksessa
 * kuten teemalla. Jos muutat niitä, muuta molemmat.
 */
export type Varit = 'kaytossa' | 'pois';

const VARIT_AVAIN = 'tk.varit';

function lueVarit(): Varit {
  try {
    return localStorage.getItem(VARIT_AVAIN) === 'pois' ? 'pois' : 'kaytossa';
  } catch {
    return 'kaytossa';
  }
}

function kirjoitaVaritDomiin(varit: Varit): void {
  document.documentElement.dataset.varit = varit;
}

let nykyisetVarit: Varit = lueVarit();
const varikuuntelijat = new Set<() => void>();

function ilmoitaVarit(): void {
  for (const kuuntelija of varikuuntelijat) kuuntelija();
}

export function asetaVarit(varit: Varit): void {
  nykyisetVarit = varit;
  try {
    localStorage.setItem(VARIT_AVAIN, varit);
  } catch {
    // Valinta jää voimaan vain tälle istunnolle. Parempi kuin kaatuminen.
  }
  kirjoitaVaritDomiin(varit);
  ilmoitaVarit();
}

// Moduulitasolla eikä kutsukohdassa: uusi funktio joka renderillä saisi
// Reactin tilaamaan uudelleen jokaisen renderin jälkeen.
function tilaaVarit(kuuntelija: () => void): () => void {
  varikuuntelijat.add(kuuntelija);
  return () => {
    varikuuntelijat.delete(kuuntelija);
  };
}

export function useVarit(): Varit {
  return useSyncExternalStore(tilaaVarit, () => nykyisetVarit);
}

window.addEventListener('storage', (tapahtuma) => {
  if (tapahtuma.key !== null && tapahtuma.key !== VARIT_AVAIN) return;
  nykyisetVarit = lueVarit();
  kirjoitaVaritDomiin(nykyisetVarit);
  ilmoitaVarit();
});
